import styled from 'styled-components'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { device } from '@/common/constants/device'
import BaseLink from '@/components/common/BaseLink'
import { userAmplitudeAnalytics } from '@/common/services/userAmplitudeAnalytics'
import { ANALYTIC_HEADER } from '@/common/constants/analytics'
import { SEARCH_COOKIE_KEY } from '@/components/Ads/AdsHeader'
import { getCookie } from 'cookies-next'
import { useTranslations } from 'next-intl'
import { useSelector } from 'react-redux'

const BLOG_LANG_LINKS = {
  ru: 'https://adheart.me/ru/blog/',
  en: 'https://adheart.me/en/blog',
  es: 'https://adheart.me/es/blog',
  pt: 'https://adheart.me/pt/blog',
  uk: 'https://adheart.me/uk/blog',
}

const TELEGRAM_SUPPORT_LINKS = {
  ru: 'https://t.me/adheart',
  en: 'https://t.me/adheart_english',
  es: 'https://t.me/adheart_english',
  pt: 'https://t.me/adheart_english',
  uk: 'https://t.me/adheart_ua',
}

const TELEGRAM_LINK = 'https://t.me/adheart'
const BLOG_LINK = '/blog'

const LinkWrapper = styled.nav`
  display: ${(props) => (props.isHeader ? 'none' : 'grid')};
  gap: 32px;

  @media ${device.tablet} {
    display: ${(props) => (props.isHeader ? 'flex' : 'none')};
    gap: 16px;
    align-items: center;
  }

  a {
    font-weight: 400;
    font-size: 1.125em;
    color: var(--grey);
    width: fit-content;
    position: relative;

    @media ${device.fromTabletTolaptop} {
      font-size: 14px;
    }

    &:hover {
      color: var(--yellow);
    }

    &:active,
    &:focus {
      color: var(--black);
    }

    &.active {
      color: var(--black);
    }

    &.active::after {
      content: '';
      position: absolute;
      bottom: -4px;
      left: 0;
      width: 100%;
      height: 5px;
      background-color: var(--yellow);

      @media ${device.tablet} {
        bottom: -8px;
      }
    }
  }
`

const HeaderNavigation = ({ isHeader, ...props }) => {
  const router = useRouter()
  const activeRoute = useMemo(() => router.pathname, [router])
  const t = useTranslations('common')
  const { locale } = router
  const searchCookie = getCookie(SEARCH_COOKIE_KEY)
  const { user } = useSelector((state) => state.user)

  const NAVIGATION_LINKS = useMemo(() => {
    return [
      {
        id: 1,
        href: searchCookie === 'classic' ? '/teasers' : '/ads',
        text: 'navigationLinkCreatives',
        locale: searchCookie !== 'classic',
        analyticEvent: 'creatives',
        showLink: true,
      },
      {
        id: 2,
        href: `/apps`,
        text: 'navigationLinkApplications',
        analyticEvent: 'applications',
        showLink: true,
      },
      {
        id: 3,
        href: searchCookie === 'classic' ? '/teasers_my' : '/ads/favorites',
        locale: searchCookie !== 'classic',
        text: 'navigationLinkSelections',
        analyticEvent: 'favorites',
        showLink: true,
      },
      {
        id: 4,
        href: '/partners',
        text: 'navigationLinkPartners',
        locale: true,
        analyticEvent: 'partners',
        showLink: true,
      },
      // {
      //   id: 5,
      //   href: 'https://t.me/adheart',
      //   text: 'navigationLinkTelegram',
      // },
      {
        id: 6,
        href: '/blog',
        text: 'blog',
        analyticEvent: 'blog',
        showLink: true,
      },
      {
        id: 7,
        href: '/premium',
        text: 'pricing',
        locale: true,
        analyticEvent: 'premium',
        showLink: !user,
      },
    ]
  }, [searchCookie, user])

  const getCorrectUrlPath = (link) => {
    let href = link.href

    if (link.href === TELEGRAM_LINK) {
      href = TELEGRAM_SUPPORT_LINKS[locale]
    }

    if (link.href === BLOG_LINK) {
      href = BLOG_LANG_LINKS[locale]
    }

    return href
  }

  const isAddedBaseLink = (link) => {
    return link.locale
  }

  const onLinkClick = (name) => {
    userAmplitudeAnalytics.trackEvent(`${ANALYTIC_HEADER}_${name}_clicked`)
  }

  return (
    <LinkWrapper isHeader={isHeader} {...props}>
      {NAVIGATION_LINKS.filter((link) => link.showLink).map((link) =>
        isAddedBaseLink(link) ? (
          <BaseLink
            onClick={() => onLinkClick(link.analyticEvent)}
            className={activeRoute === link.href ? 'active' : ''}
            key={link.id}
            href={getCorrectUrlPath(link)}
            isChangePath={false}
          >
            {t(`${link.text}`)}
          </BaseLink>
        ) : (
          <a
            onClick={() => onLinkClick(link.analyticEvent)}
            key={link.id}
            href={getCorrectUrlPath(link)}
          >
            {t(`${link.text}`)}
          </a>
        )
      )}
    </LinkWrapper>
  )
}

export default HeaderNavigation
